import axiosInstance from "./axiosInstance";
import { HealthStatus, SureProjectId } from "../types/log";
import { CronJob, CronJobDetail, JobRun, RunFailure } from "../types/job-run";
import { CheckKind, HealthCheck, HealthChecksOverview } from "../types/health-check";

export type QueryParams = Record<string, string | number | undefined>;
type List<T> = { totalCount: number; data: T[] };

export type ApiLog = {
  id: number;
  platform: SureProjectId;
  status: HealthStatus;
  type: string;
  link: string;
  message: string | null;
  statusCode: number | null;
  responseTime: number | null;
  environment: string | null;
  routeCategory: "payment" | "public" | "secret" | "general" | null;
  correlationId: string | null;
  refs: Record<string, string> | null;
  count: number;
  fingerprint: string | null;
  data: Record<string, any> | null;
  createdAt: string;
};

export type ApiLogDetail = ApiLog & {
  issue: { id: number; count: number; firstSeen: string; lastSeen: string; state: string } | null;
  related?: ApiLog[];
};

// ----------------------- POLICY REF OF A LOG (CONTEXT OR EXPLICIT) ------------------------------------
export const policyOf = (log: Pick<ApiLog, "refs">) => log.refs?.policyRef ?? log.refs?.policyNumber ?? null;

export type LogGroup = {
  id: number;
  platform: SureProjectId;
  fingerprint: string;
  statusCode: number;
  status: HealthStatus;
  type: string;
  message: string;
  route: string;
  count: number;
  firstSeen: string;
  lastSeen: string;
  state: "open" | "acknowledged" | "muted" | "resolved";
  environment: string | null;
  routeCategory: ApiLog["routeCategory"];
};

export const PAYMENT_DECLINE_TYPE = "payment_decline";

export type AlertSource = "api" | "cron" | "health";
export type AlertView = "open" | "muted" | "resolved";
export type AlertAction = "acknowledge" | "mute" | "resolve" | "reopen";

export type AlertItem = {
  id: string;
  source: AlertSource;
  severity: "critical" | "warning";
  state: "open" | "acknowledged" | "muted" | "resolved";
  platform: SureProjectId;
  title: string;
  detail: string;
  count: number;
  firstSeen: string;
  lastSeen: string;
  ref: Record<string, string | number | null>;
};

export type PlatformOverview = {
  platform: SureProjectId;
  label: string;
  status: HealthStatus;
  errorRate: number;
  apiEvents24h: number;
  apiErrors24h: number;
  p95Latency: number | null;
  sparkline: number[];
  openAlerts: number;
  jobs: { total: number; bad: number; running: number };
  pulse: { configured: boolean; status: string | null; checkedAt: string | null; recent: { status: string; createdAt: string }[] };
  health: { status: string | null; score: number | null; checkedAt: string | null };
};

export type Overview = {
  platforms: PlatformOverview[];
  totals: { openAlerts: number; platformsDown: number; jobs: number; jobsFailing: number; apiEvents24h: number; apiErrors24h: number; runs24h: number };
  runStatus24h: Record<string, number>;
  series: { timestamp: string; healthy: number; unhealthy: number; critical: number }[];
  generatedAt: string;
};

// ----------------------- DROP EMPTY PARAMS ------------------------------------
const clean = (params: QueryParams) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== "" && value != null));

const get = async <T>(url: string, params: QueryParams = {}) => (await axiosInstance.get<T>(url, { params: clean(params) })).data;

export const fetchOverview = async () => (await get<{ data: Overview }>("/v2/overview")).data;

export const fetchLogs = (params: QueryParams) => get<List<ApiLog>>("/v2/logs", params);
export const fetchLogGroups = (params: QueryParams) => get<List<LogGroup> & { kinds: { errors: number; declines: number } }>("/v2/log-groups", params);
export const fetchLog = async (id: number) => (await get<{ data: ApiLogDetail }>(`/v2/logs/${id}`)).data;

export const fetchAlerts = (params: QueryParams) => get<List<AlertItem> & { counts: Record<AlertView, number> }>("/v2/alerts", params);
export const updateAlert = async (id: string, action: AlertAction) =>
  (await axiosInstance.patch<{ data: { id: string; state: AlertItem["state"] } }>(`/v2/alerts/${encodeURIComponent(id)}`, { action })).data.data;

export const fetchCronJobs = (params: QueryParams) => get<List<CronJob>>("/v2/cron-jobs", params);
export const fetchCronJob = async (id: number) => (await get<{ data: CronJobDetail }>(`/v2/cron-jobs/${id}`, { days: 7 })).data;
export const fetchJobRuns = (id: number, params: QueryParams) => get<List<JobRun>>(`/v2/cron-jobs/${id}/runs`, params);
export const fetchRunFailures = async (runId: number) => (await get<{ data: RunFailure[] }>(`/v2/cron-jobs/runs/${runId}/failures`)).data;

export const fetchHealth = async (kind: CheckKind) => (await get<{ data: HealthChecksOverview }>("/v2/health-checks/latest", { kind })).data;
export const fetchHealthHistory = (params: QueryParams) => get<List<HealthCheck>>("/v2/health-checks/history", params);
export const runHealthCheck = async (kind: CheckKind, platform?: SureProjectId) =>
  (await axiosInstance.post("/v2/health-checks/run", clean({ kind, platform }), { timeout: 90000 })).data;
